import { useEffect, useState } from "react";
import { Loader2, Share2, Download, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { renderBadgeImage } from "@/lib/badgeImage";

const FORMATS = [{ id: "story", label: "Story (Hochformat)" }, { id: "square", label: "Post (Quadrat)" }];

const download = (url, name) => { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); };

function useShareImage(render, deps) {
  const [img, setImg] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let url;
    setImg(null);
    let alive = true;
    const fail = setTimeout(() => alive && setImg((x) => x || { error: true }), 20000);
    render().then((blob) => { if (!alive) return; url = URL.createObjectURL(blob); setImg({ blob, url }); })
      .catch(() => { if (alive) { setImg({ error: true }); toast.error("Bild konnte nicht erstellt werden – bitte nochmal versuchen"); } })
      .finally(() => clearTimeout(fail));
    return () => { alive = false; clearTimeout(fail); if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return [img, () => setTick((t) => t + 1)];
}

export function ShareImageDialog({ title, description, render, deps, fileBase, msg, alt, prefix, onClose }) {
  const [format, setFormat] = useState("story");
  const [img, retry] = useShareImage(() => render(format), [...deps, format]);
  const ready = img?.url ? img : null;
  const fileName = `${fileBase}-${format}.png`;

  const share = async () => {
    const file = new File([ready.blob], fileName, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], text: msg, title: alt }); }
      catch (err) { if (err?.name !== "AbortError") { console.error("Teilen fehlgeschlagen", err); toast.error("Teilen nicht möglich – das Bild wird stattdessen gespeichert"); download(ready.url, fileName); } }
      return;
    }
    download(ready.url, fileName);
    toast.success("Bild gespeichert – jetzt in Instagram oder WhatsApp hochladen");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md max-h-[92vh] overflow-y-auto" data-testid={`${prefix}-share-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 p-1 rounded-full bg-rose-50">
          {FORMATS.map((f) => (
            <button key={f.id} onClick={() => setFormat(f.id)} data-testid={`${prefix}-share-format-${f.id}`}
              className={`flex-1 h-9 rounded-full text-sm font-semibold transition-colors ${format === f.id ? "bg-white text-rose-600 shadow-sm" : "text-slate-500"}`}>{f.label}</button>
          ))}
        </div>
        <div className={`mx-auto rounded-2xl overflow-hidden bg-rose-100 flex items-center justify-center ${format === "story" ? "w-48 aspect-[9/16]" : "w-64 aspect-square"}`}>
          {ready ? <img src={ready.url} alt={alt} data-testid={`${prefix}-share-preview`} className="w-full h-full object-cover" />
            : img?.error ? <button onClick={retry} data-testid={`${prefix}-share-retry`} className="px-4 h-10 rounded-full bg-white text-rose-600 text-sm font-bold shadow">Nochmal versuchen</button>
            : <Loader2 className="w-6 h-6 animate-spin text-rose-500" />}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={share} disabled={!ready} data-testid={`${prefix}-share-btn`} className="col-span-2 h-12 rounded-full pink-gradient text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"><Share2 className="w-4 h-4" /> Bild teilen</button>
          <button onClick={() => download(ready.url, fileName)} disabled={!ready} data-testid={`${prefix}-download-btn`} className="h-11 rounded-full bg-slate-100 hover:bg-slate-200 font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"><Download className="w-4 h-4" /> Speichern</button>
          <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-whatsapp-btn`} className="h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        </div>
        <p className="text-xs text-slate-400 text-center">Tipp: Auf dem Handy „Bild teilen“ tippen und Instagram oder TikTok wählen – oder das Bild speichern und in deiner Story hochladen.</p>
      </DialogContent>
    </Dialog>
  );
}

export function BadgeShare({ badge, Icon, user, onClose }) {
  const link = `${window.location.origin}/registrieren?ref=${user.referral_code}`;
  return (
    <ShareImageDialog prefix="badge" title="Abzeichen teilen" alt={badge.title} fileBase={`itzcouple-${badge.id}`} onClose={onClose}
      description={`Teile „${badge.title}“ in deiner Story oder per WhatsApp. Dein Einladungslink ist mit drauf, damit du für jeden neuen Freund Punkte bekommst.`}
      msg={`Ich habe das Abzeichen „${badge.title}“ in der itzPlay Community freigeschaltet! 🏅 Mach mit: ${link}`}
      render={(format) => renderBadgeImage({ badge, Icon, user, link, format })} deps={[badge, Icon, user, link]} />
  );
}
